// frontend/src/pages/Questionnaire.js
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { fetchQuestionnaire } from '../api';
import MentalLoadScreen from '../components/MentalLoadScreen';
import ReadableText from '../components/ReadableText';
import './Questionnaire.css';
import { useI18n } from '../i18n';

const QUESTION_TRANSITION_MS = 220;
const COMPLETION_OVERLAY_DELAY_MS = 2000;

function getQuestionTransitionDelay() {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return 0;
  }

  return QUESTION_TRANSITION_MS;
}

function QuestionnaireDiceOverlay({ show, message }) {
  if (!show || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="questionnaire-dice-overlay"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <MentalLoadScreen loading={true} message={message} />
    </div>,
    document.body
  );
}

export default function Questionnaire({ onComplete, onSkip }) {
  const { t } = useI18n();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [isQuestionTransitioning, setIsQuestionTransitioning] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const transitionTimeoutRef = useRef(null);
  const completionTimeoutRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetchQuestionnaire();
        setQuestions(res.data.questions || []);
      } catch (err) {
        console.error('Error loading questionnaire:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(transitionTimeoutRef.current);
      clearTimeout(completionTimeoutRef.current);
    };
  }, []);

  if (loading) {
    return (
      <div id="questionnaire-main" className="questionnaire-container">
        <QuestionnaireDiceOverlay show={true} message={t('questionnaire.loading')} />
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="questionnaire-container">
        <div className="question-card">
          <p>{t('questionnaire.empty')}</p>
          <button onClick={onSkip} className="skip-button">{t('common.close')}</button>
        </div>
      </div>
    );
  }

  const current = questions[currentIndex];
  const isBusy = completing || isQuestionTransitioning;

  const handleSelectOption = (opt) => {
    if (isBusy) return;
    setCurrentAnswer(opt);
    setShowTextInput(false);
  };

  const toggleTextInput = () => {
    if (isBusy) return;
    setShowTextInput(true);
    setCurrentAnswer('');
  };

  const toggleOptions = () => {
    if (isBusy) return;
    setShowTextInput(false);
    setCurrentAnswer('');
  };

  const handleNext = async () => {
    if (isBusy || !currentAnswer || currentAnswer.trim() === '') return;

    const answerToSave = currentAnswer.trim();
    const newAnswers = { ...answers, [current.id]: answerToSave };
    setAnswers(newAnswers);

    if (currentIndex === questions.length - 1) {
      setCompleting(true);
      completionTimeoutRef.current = setTimeout(() => {
        onComplete(newAnswers);
      }, COMPLETION_OVERLAY_DELAY_MS);
      return;
    }

    const transitionDelay = getQuestionTransitionDelay();

    if (transitionDelay === 0) {
      setCurrentIndex(idx => idx + 1);
      setCurrentAnswer('');
      setShowTextInput(false);
      return;
    }

    setIsQuestionTransitioning(true);
    transitionTimeoutRef.current = setTimeout(() => {
      setCurrentIndex(idx => idx + 1);
      setCurrentAnswer('');
      setShowTextInput(false);
      setIsQuestionTransitioning(false);
    }, transitionDelay);
  };

  const hasOptions = Array.isArray(current.options) && current.options.length > 0;
  const isTextMode = showTextInput || !hasOptions;
  const isNextDisabled = isBusy || !currentAnswer || currentAnswer.trim() === '';

  return (
    <div className="questionnaire-container">
      <QuestionnaireDiceOverlay show={completing} message={t('questionnaire.completing')} />
      <div className={`question-card ${isQuestionTransitioning ? 'is-transitioning' : ''}`} aria-busy={isBusy}>
        <h2 id="questionnaire-current-question"><ReadableText text={current.text} label={t('questionnaire.readQuestion')}>{current.text}</ReadableText></h2>

        <div className="answer-section">
          {!isTextMode ? (
            <div className="multiple-options">
              {current.options.map((opt, i) => (
                <button
                  id={`btn-quest-opt-${i}`} data-agent-question-option={opt}
                  key={i}
                  onClick={() => handleSelectOption(opt)}
                  className={`option-btn ${currentAnswer === opt ? 'selected' : ''}`}
                  disabled={isBusy}
                >
                  {opt}
                </button>
              ))}
              <button className="toggle-input-btn" onClick={toggleTextInput} disabled={isBusy}>
                {t('questionnaire.ownAnswer')}
              </button>
            </div>
          ) : (
            <div className="open-input">
              <textarea id="questionnaire-answer-input"
                rows={4}
                value={currentAnswer}
                onChange={e => setCurrentAnswer(e.target.value)}
                placeholder={t('questionnaire.answerPlaceholder')}
                autoFocus
                disabled={isBusy}
              />
              {hasOptions && (
                <button className="toggle-options-btn" onClick={toggleOptions} disabled={isBusy}>
                  {t('questionnaire.backToOptions')}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="question-controls">
          <button id="btn-quest-skip" onClick={onSkip} className="skip-button" disabled={isBusy}>{t('common.skip')}</button>
          <button
            id="btn-quest-next"
            onClick={handleNext}
            className="next-button"
            disabled={isNextDisabled}
          >
            {currentIndex === questions.length - 1 ? t('common.finish') : t('common.next')}
          </button>
        </div>

        <div className="progress">
          {t('questionnaire.progress', { current: currentIndex + 1, total: questions.length })}
        </div>
      </div>
    </div>
  );
}
